// src/components/OrganizationPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Building2, Users, Trash2, UserPlus, Settings2, ChevronDown } from 'lucide-react';
import { getCookie } from '../utils/utils';
import InviteUsersModal from './InviteUsersModal';
import ManageTeamsModal from './ManageTeamsModal';

interface OrgContext {
  org: { id: number; name: string } | null;
  tier: { name: string | null; max_blueprints: number | null; max_creators: number | null; max_viewers: number | null };
  limits: { projects_used: number; blueprints_used: number; creators_used: number; viewers_used: number };
  is_free_tier: boolean;
  can_show_org_section: boolean;
  can_show_user_management: boolean;
  can_assign_creator_viewer: boolean;
  can_assign_catalyx_user: boolean;
  can_manage_teams?: boolean;
}

interface TeamInfo {
  id: number;
  name: string;
  is_default: boolean;
}

interface OrgMember {
  id: number;
  user: number;
  user_email: string;
  user_name: string;
  role: number;
  role_name: string;
  role_key: string;
  is_protected?: boolean;
  team_ids?: number[];
}

interface OrgRole {
  id: number;
  name: string;
  key: string;
}

const OrganizationPage: React.FC = () => {
  const [orgContext, setOrgContext] = useState<OrgContext | null>(null);
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);
  const [orgRoles, setOrgRoles] = useState<OrgRole[]>([]);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orgCredits, setOrgCredits] = useState<string | null>(null);
  const [orgCreditsUnlimited, setOrgCreditsUnlimited] = useState(false);
  const [updatingMemberId, setUpdatingMemberId] = useState<number | null>(null);
  const [removingMemberId, setRemovingMemberId] = useState<number | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showManageTeamsModal, setShowManageTeamsModal] = useState(false);
  const [orgTeams, setOrgTeams] = useState<TeamInfo[]>([]);
  const [teamDropdownOpenFor, setTeamDropdownOpenFor] = useState<number | null>(null);
  const teamButtonRef = useRef<HTMLButtonElement>(null);
  const teamDropdownRef = useRef<HTMLDivElement>(null);
  const [teamDropdownPos, setTeamDropdownPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    fetchOrgContext();
    fetchCurrentUser();
    fetchOrgCredits();
  }, []);

  useEffect(() => {
    if (orgContext?.can_show_user_management && orgContext?.org) {
      fetchOrgMembers();
      fetchOrgRoles();
      fetchOrgTeams();
    }
  }, [orgContext?.can_show_user_management, orgContext?.org?.id]);

  // Close team dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check if click is inside the dropdown menu or the trigger button
      if (teamDropdownRef.current && teamDropdownRef.current.contains(target)) return;
      if (teamButtonRef.current && teamButtonRef.current.contains(target)) return;
      setTeamDropdownOpenFor(null);
      setTeamDropdownPos(null);
    };
    if (teamDropdownOpenFor !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [teamDropdownOpenFor]);

  const fetchOrgContext = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/user/org-context/', {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setOrgContext(data);
      } else {
        setError('Failed to load organization');
      }
    } catch (err) {
      setError('An error occurred while loading your organization');
      console.error('Org context fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrgCredits = async () => {
    try {
      const response = await fetch('/api/user/balance/', {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setOrgCreditsUnlimited(Boolean(data.is_unlimited));
        setOrgCredits(data.total_credits ?? data.credits_marketplace ?? null);
      }
    } catch (err) {
      console.error('Balance fetch error:', err);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch('/api/user/profile/', {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentUserId(data.id);
      }
    } catch (err) {
      console.error('Profile fetch error:', err);
    }
  };

  const fetchOrgMembers = async () => {
    if (!orgContext?.org) return;
    try {
      const response = await fetch(`/api/org-members/?org_id=${orgContext.org.id}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setOrgMembers(data);
      }
    } catch (err) {
      console.error('Org members fetch error:', err);
    }
  };

  const fetchOrgRoles = async () => {
    try {
      const response = await fetch('/api/org-roles/', {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setOrgRoles(data);
      }
    } catch (err) {
      console.error('Org roles fetch error:', err);
    }
  };

  const fetchOrgTeams = async () => {
    if (!orgContext?.org) return;
    try {
      const response = await fetch(`/api/teams/?org_id=${orgContext.org.id}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setOrgTeams(data);
      }
    } catch (err) {
      console.error('Org teams fetch error:', err);
    }
  };

  const handleTeamToggle = async (memberId: number, userId: number, teamId: number, isCurrentlyIn: boolean) => {
    if (!orgContext?.org) return;
    const member = orgMembers.find((m) => m.id === memberId);
    if (!member) return;
    const currentTeamIds = member.team_ids || [];
    let newTeamIds: number[];
    if (isCurrentlyIn) {
      newTeamIds = currentTeamIds.filter((id) => id !== teamId);
    } else {
      newTeamIds = [...currentTeamIds, teamId];
    }
    try {
      const csrfToken = getCookie('csrftoken');
      const response = await fetch('/api/teams/update-user-teams/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken || '',
        },
        body: JSON.stringify({
          org_id: orgContext.org.id,
          user_id: userId,
          team_ids: newTeamIds,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setOrgMembers((prev) =>
          prev.map((m) =>
            m.user === userId ? { ...m, team_ids: data.team_ids } : m
          )
        );
      } else {
        const err = await response.json();
        setError(err?.error || 'Failed to update team membership');
      }
    } catch (err) {
      setError('Failed to update team membership');
    }
  };

  const getAssignableRoles = () => {
    const assignableKeys = ['creator', 'viewer', 'admin'];
    return orgRoles.filter((r) => assignableKeys.includes(r.key.toLowerCase()));
  };

  const getRoleWeight = (roleKey: string): number => {
    const key = roleKey.toLowerCase();
    if (key === 'account_manager') return 0;
    if (key === 'admin') return 1;
    if (['catalyx-user', 'catalyx_user', 'catalyx'].includes(key)) return 2;
    if (key === 'creator') return 3;
    if (key === 'viewer') return 4;
    return 5;
  };

  const sortedMembers = [...orgMembers].sort(
    (a, b) => getRoleWeight(a.role_key) - getRoleWeight(b.role_key),
  );

  const CATALYX_ROLE_KEYS = ['catalyx-user', 'catalyx_user', 'catalyx'];
  const NON_TEAM_ROLE_KEYS = ['account_manager', 'admin', 'catalyx-user', 'catalyx_user', 'catalyx'];

  const shouldShowTeamSelector = (roleKey: string): boolean => {
    return !NON_TEAM_ROLE_KEYS.includes(roleKey.toLowerCase());
  };

  const handleRoleChange = async (membershipId: number, roleId: number) => {
    const newRole = orgRoles.find((r) => r.id === roleId);
    const member = orgMembers.find((m) => m.id === membershipId);
    if (
      newRole &&
      member &&
      CATALYX_ROLE_KEYS.includes(newRole.key.toLowerCase()) &&
      !member.user_email.toLowerCase().endsWith('@thecatalyx.com')
    ) {
      setError('Cannot give catalyx permissions to a non catalyx user.');
      return;
    }

    if (
      !window.confirm(
        'This changes the user\'s role in every organization they belong to. Continue?',
      )
    ) {
      return;
    }

    setUpdatingMemberId(membershipId);
    try {
      const csrfToken = getCookie('csrftoken');
      const response = await fetch(`/api/org-memberships/${membershipId}/`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken || '',
        },
        body: JSON.stringify({ role: roleId }),
      });
      if (response.ok) {
        await fetchOrgMembers();
      } else {
        const err = await response.json();
        setError(err?.error || 'Failed to update role');
      }
    } catch (err) {
      setError('Failed to update role');
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleRemoveMember = async (membershipId: number) => {
    if (!window.confirm('Remove this user from the organization?')) return;
    setRemovingMemberId(membershipId);
    try {
      const csrfToken = getCookie('csrftoken');
      const response = await fetch(`/api/org-memberships/${membershipId}/`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'X-CSRFToken': csrfToken || '' },
      });
      if (response.ok) {
        await fetchOrgMembers();
      } else {
        const err = await response.json();
        setError(err?.error || 'Failed to remove member');
      }
    } catch (err) {
      setError('Failed to remove member');
    } finally {
      setRemovingMemberId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading organization...</p>
        </div>
      </div>
    );
  }

  if (!orgContext?.can_show_org_section || !orgContext.org) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">My Organization</h1>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">You are not part of an organization yet.</p>
          <p className="text-sm text-gray-500 mt-2">Switch to an organization in Settings to view organization details.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">My Organization</h1>
        <p className="text-gray-600 mt-2">View your organization details and manage members</p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Organization Info */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {orgContext.org.name}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Credits</p>
                <p className="text-sm font-medium text-gray-900">
                  {orgCreditsUnlimited ? 'Unlimited' : (orgCredits ?? '—')}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Projects</p>
                <p className="text-sm font-medium text-gray-900">
                  {orgContext.limits.projects_used}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Blueprints</p>
                <p className="text-sm font-medium text-gray-900">
                  {orgContext.limits.blueprints_used}
                  {orgContext.tier.max_blueprints != null && (
                    <span className="text-gray-500"> / {orgContext.tier.max_blueprints}</span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Creators</p>
                <p className="text-sm font-medium text-gray-900">
                  {orgContext.limits.creators_used}
                  {orgContext.tier.max_creators != null && (
                    <span className="text-gray-500"> / {orgContext.tier.max_creators}</span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Viewers</p>
                <p className="text-sm font-medium text-gray-900">
                  {orgContext.limits.viewers_used}
                  {orgContext.tier.max_viewers != null && (
                    <span className="text-gray-500"> / {orgContext.tier.max_viewers}</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* User Management */}
        {orgContext.can_show_user_management && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  User Management
                </h3>
                <div className="flex items-center gap-2">
                  {orgContext.can_manage_teams && (
                    <button
                      onClick={() => setShowManageTeamsModal(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <Settings2 className="w-4 h-4" />
                      Manage Teams
                    </button>
                  )}
                  {(orgContext.can_assign_creator_viewer || orgContext.can_assign_catalyx_user) && (
                    <button
                      onClick={() => setShowInviteModal(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                    >
                      <UserPlus className="w-4 h-4" />
                      Invite Users
                    </button>
                  )}
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                      {orgTeams.length > 0 && (
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teams</th>
                      )}
                      {orgContext.can_assign_creator_viewer && (
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedMembers.map((member) => (
                      <tr key={`${member.id}-${member.user}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{member.user_name || member.user_email}</p>
                            <p className="text-xs text-gray-500">{member.user_email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {orgContext.can_assign_creator_viewer && member.user !== currentUserId && !member.is_protected ? (
                            <select
                              value={member.role}
                              onChange={(e) => handleRoleChange(member.id, parseInt(e.target.value, 10))}
                              disabled={updatingMemberId === member.id}
                              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-indigo-500"
                            >
                              {getAssignableRoles().map((role) => (
                                <option key={role.id} value={role.id}>
                                  {role.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-sm text-gray-700">{member.role_name}</span>
                          )}
                        </td>
                        {orgTeams.length > 0 && (
                          <td className="px-4 py-3">
                            {orgContext.can_manage_teams && member.user !== currentUserId && shouldShowTeamSelector(member.role_key) ? (
                              <div className="relative">
                                <button
                                  ref={teamDropdownOpenFor === member.id ? teamButtonRef : undefined}
                                  onClick={(e) => {
                                    if (teamDropdownOpenFor === member.id) {
                                      setTeamDropdownOpenFor(null);
                                      setTeamDropdownPos(null);
                                    } else {
                                      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                      setTeamDropdownPos({ top: rect.bottom + 4, left: rect.left });
                                      setTeamDropdownOpenFor(member.id);
                                    }
                                  }}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded-md hover:bg-gray-50 max-w-[160px]"
                                >
                                  <span className="truncate">
                                    {(member.team_ids || []).length === 0
                                      ? 'No teams'
                                      : orgTeams
                                          .filter((t) => (member.team_ids || []).includes(t.id))
                                          .map((t) => t.name)
                                          .join(', ') || 'No teams'}
                                  </span>
                                  <ChevronDown className="w-3 h-3 flex-shrink-0" />
                                </button>
                                {teamDropdownOpenFor === member.id && teamDropdownPos && (
                                  <div
                                    ref={teamDropdownRef}
                                    className="fixed z-50 w-48 bg-white border border-gray-200 rounded-md shadow-lg py-1"
                                    style={{ top: teamDropdownPos.top, left: teamDropdownPos.left }}
                                  >
                                    {orgTeams.map((team) => {
                                      const isIn = (member.team_ids || []).includes(team.id);
                                      return (
                                        <label
                                          key={team.id}
                                          className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isIn}
                                            onChange={() => handleTeamToggle(member.id, member.user, team.id, isIn)}
                                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                          />
                                          <span className="truncate">{team.name}</span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-600">
                                {!shouldShowTeamSelector(member.role_key)
                                  ? '-'
                                  : (member.team_ids || []).length === 0
                                    ? '-'
                                    : orgTeams
                                        .filter((t) => (member.team_ids || []).includes(t.id))
                                        .map((t) => t.name)
                                        .join(', ')}
                              </span>
                            )}
                          </td>
                        )}
                        {orgContext.can_assign_creator_viewer && (
                          <td className="px-4 py-3 text-right">
                            {member.user !== currentUserId && !member.is_protected ? (
                              <button
                                onClick={() => handleRemoveMember(member.id)}
                                disabled={removingMemberId === member.id}
                                className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 disabled:opacity-50"
                                title="Remove from organization"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400">
                                {member.user === currentUserId ? 'You' : ''}
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {orgMembers.length === 0 && (
                  <div className="px-4 py-8 text-center text-sm text-gray-500">No members in this organization.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Invite Users Modal */}
      {showInviteModal && orgContext.org && (
        <InviteUsersModal
          orgId={orgContext.org.id}
          canAssignCreatorViewer={orgContext.can_assign_creator_viewer}
          canAssignCatalyxUser={orgContext.can_assign_catalyx_user}
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => fetchOrgMembers()}
        />
      )}

      {/* Manage Teams Modal */}
      {showManageTeamsModal && orgContext.org && (
        <ManageTeamsModal
          isOpen={showManageTeamsModal}
          orgId={orgContext.org.id}
          onClose={() => setShowManageTeamsModal(false)}
          onTeamsChanged={() => {
            fetchOrgTeams();
            fetchOrgMembers();
          }}
        />
      )}
    </div>
  );
};

export default OrganizationPage;
